import {Observable, throwError, timer} from 'rxjs';
import {mergeMap} from 'rxjs/operators';

/**
 * Strategy for operator retryWhen that will retry maxRetryAttempts time while response http code is in statusCode
 * Use this function inside a retryWhen operator :
 * <pre>@code
 *     httpService.get().pipe(
 *          retryWhen(httpRetry())
 *     ).subscribe()
 * </pre>
 * This method is a modify version of the RxJs docs (Customizable retry with increased duration)
 * @see https://www.learnrxjs.io/learn-rxjs/operators/error_handling/retrywhen
 *
 * @param maxRetryAttempts number of time the http call will be perform. 15 by default.
 * @param delayBetweenAnAttemptInMs delay between two attempts. 1s by default.
 * @param statusCodes retry if statusCodes contains the http response code. 404 only by default.
 */
export function httpRetry({maxAttempts = 15, delayBetweenAnAttemptInMs = 1000, statusCodes = [404]}: HttpRetryStrategy = {}) {
    return (attempts: Observable<any>) => {
        return attempts.pipe(
            mergeMap((error, index) => {
                const numberOfAttempts = index + 1;

                // if maximum number of retries have been met
                // or response is a status code we don't wish to retry, throw error
                const isMaxAttemptsReach = numberOfAttempts > maxAttempts;
                const isANonRetryError = statusCodes.find(e => e !== error.status);

                if (isMaxAttemptsReach || isANonRetryError) {
                    return throwError(error);
                }

                // wait before retry
                return timer(delayBetweenAnAttemptInMs);
            })
        );
    };
}

/**
 * Interface for use name parameter for function httpRetry
 */
export interface HttpRetryStrategy {
    maxAttempts?: number;
    delayBetweenAnAttemptInMs?: number;
    statusCodes?: Array<Number>;
}
